import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Rarity } from '#src/__generated__/globalTypes.ts';
import { getEdgeRectangleShapeRarityGradients } from '#src/lib/colors.ts';
import { isType, withFragments } from '#src/lib/gql/index.ts';
import { isCraftingClue, isXPCurrency } from '#src/lib/rewards.tsx';
import { inGameCurrencyToRarity } from '#src/lib/scarcity.ts';

import type { getAnyRewardConfigColors_anyRewardConfigInterface } from './__generated__/utils.graphql.ts';

const EXPERIENCE_COLORS = {
  bg: '#1A1C0E',
  border:
    'linear-gradient(to bottom, transparent 0%, #1A1C0E 70%), linear-gradient(to right, #AA78FF, #D6E456, #AA78FF, #D6E456, #AA78FF, #D6E456, #AA78FF)',
};

export const getAnyRewardConfigColors = withFragments(
  ({
    anyRewardConfig,
    rarity,
  }: {
    anyRewardConfig: getAnyRewardConfigColors_anyRewardConfigInterface;
    rarity?: Rarity | 'mix';
  }) => {
    switch (anyRewardConfig.__typename) {
      case 'CardPacksRewardConfig':
        return getEdgeRectangleShapeRarityGradients(Rarity.common);
        break;
      case 'CardRewardConfig':
        return getEdgeRectangleShapeRarityGradients(
          rarity || anyRewardConfig.configRarity
        );
        break;
      case 'CardShardRewardConfig':
        return getEdgeRectangleShapeRarityGradients(
          rarity || anyRewardConfig.configRarity
        );
        break;
      case 'ConversionCreditRewardConfig':
        return {
          bg: '#111F1C',
          border:
            'linear-gradient(to bottom, transparent 0%, #111F1C 70%), linear-gradient(to right, #78FFE8, #EDFF95, #78FFE8, #EDFF95, #78FFE8, #EDFF95, #78FFE8)',
        };
      case 'MonetaryRewardConfig':
        return {
          bg: '#1F121C',
          border:
            'linear-gradient(to bottom, transparent 0%, #1F121C 70%), linear-gradient(to right, #78C2FF, #FF86E1, #78C2FF, #FF86E1, #78C2FF, #FF86E1, #78C2FF)',
        };
      case 'ExperienceRewardConfig':
        return EXPERIENCE_COLORS;
      case 'DeliverableItemRewardConfig':
        if (
          isType(anyRewardConfig.deliverableItem, 'WearableDeliverableItem') ||
          isType(anyRewardConfig.deliverableItem, 'JerseyDeliverableItem')
        ) {
          return getEdgeRectangleShapeRarityGradients(Rarity.common);
        }
        if (
          isType(anyRewardConfig.deliverableItem, 'ExperienceDeliverableItem')
        ) {
          return EXPERIENCE_COLORS;
        }
        if (isType(anyRewardConfig.deliverableItem, 'TicketsDeliverableItem')) {
          return {
            bg: '#07151F',
            border:
              'linear-gradient(to bottom, transparent 0%, #07151F 70%), linear-gradient(to right, #78C2FF, #FF86E1, #78C2FF, #FF86E1, #78C2FF, #FF86E1, #78C2FF)',
          };
        }
        break;
      case 'ProbabilisticBundleRewardConfig':
        return getEdgeRectangleShapeRarityGradients(
          anyRewardConfig.probabilisticBundleConfig.rarity
        );
      case 'InGameCurrencyRewardConfig':
        if (isXPCurrency(anyRewardConfig.currency)) {
          return getEdgeRectangleShapeRarityGradients(
            (rarity === 'mix' && rarity) ||
              inGameCurrencyToRarity[anyRewardConfig.currency] ||
              Rarity.common
          );
        }
        if (isCraftingClue(anyRewardConfig.currency)) {
          return {
            bg: 'linear-gradient(0deg, rgba(140, 228, 86, 0.1), rgba(140, 228, 86, 0.1)), linear-gradient(0deg,var(--c-black), var(--c-black))',
            border:
              'linear-gradient(to bottom,transparent 0%, #111F1C 70%), linear-gradient(to right, #EDFF95 0%, #78FFE9 25%, #EDFF95 50%, #78FFE8 75%, #EDFF95 100%)',
          };
        }
        break;
      case 'ShopItemRewardConfig':
        return {
          bg: '',
          border: '',
        };
    }

    return {
      bg: '',
      border: '',
    };
  },
  {
    anyRewardConfigInterface: gql`
      fragment getAnyRewardConfigColors_anyRewardConfigInterface on AnyRewardConfigInterface {
        id
        ... on CardRewardConfig {
          id
          configRarity
        }
        ... on CardShardRewardConfig {
          id
          configRarity
        }
        ... on ProbabilisticBundleRewardConfig {
          id
          probabilisticBundleConfig {
            id
            rarity
          }
        }
        ... on InGameCurrencyRewardConfig {
          id
          currency
        }
        ... on ShopItemRewardConfig {
          id
          shopItem {
            id
            pictureUrl
          }
        }
        ... on DeliverableItemRewardConfig {
          id
          deliverableItem {
            slug
          }
        }
      }
    ` as TypedDocumentNode<getAnyRewardConfigColors_anyRewardConfigInterface>,
  }
);
